import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useOutlet } from "react-router-dom";
import type {
  FacebookConnectionResponse,
  InstagramConnectionResponse,
  OrganizationMemberRoleName,
  WhatsAppConnectionResponse,
} from "@multi-chat/shared";
import { isApiError } from "../../api/client.js";
import { getFacebookConnection } from "../../api/facebook-api.js";
import { getInstagramConnection } from "../../api/instagram-api.js";
import { getConnection as getWhatsAppConnection } from "../../api/whatsapp-api.js";
import { Sheet, SheetContent } from "../../components/ui/sheet/index.js";
import { CONTACT_SHEET_OVERLAY_MOTION_CLASS } from "../contacts/contacts-table-presentation.js";
import { AnimatePresence, motion, useReducedMotion } from "../../lib/motion.js";
import { ChannelsSidebar } from "./ChannelsSidebar.js";
import { ChannelsWorkspaceHeader } from "./ChannelsWorkspaceHeader.js";
import { AddChannelDialog } from "./AddChannelDialog.js";
import {
  applyChannelDetailHeader,
  ChannelDetailRouteSkeleton,
  channelDetailPlatform,
  resolveChannelDetailHeader,
  type ChannelDetailDisconnectAction,
  type ChannelDetailHeaderState,
  type RegisteredChannelDetailHeader,
} from "./channel-detail-header.js";
export type { ChannelDetailDisconnectAction, ChannelDetailHeaderState };

export type ChannelDirectory = {
  organizationId: number;
  role: OrganizationMemberRoleName;
  whatsapp: WhatsAppConnectionResponse | null;
  facebook: FacebookConnectionResponse | null;
  instagram: InstagramConnectionResponse | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
  openMobileNav: () => void;
  mobileNavOpen: boolean;
  openAddChannel: () => void;
  addChannelOpen: boolean;
  setAddChannelOpen: (open: boolean) => void;
  detailsPlatform: "facebook" | "instagram" | null;
  openDetails: (platform: "facebook" | "instagram") => void;
  closeDetails: () => void;
  detailHeader: ChannelDetailHeaderState | null;
  detailHeaderPending: boolean;
  setRegisteredDetailHeader: React.Dispatch<
    React.SetStateAction<RegisteredChannelDetailHeader | null>
  >;
};

export const ChannelDirectoryContext = createContext<ChannelDirectory | null>(null);

export function useChannelDirectory(): ChannelDirectory {
  const value = useContext(ChannelDirectoryContext);
  if (!value) {
    throw new Error("useChannelDirectory must be used inside ChannelsWorkspaceLayout");
  }
  return value;
}

/**
 * Registers the channel-detail breadcrumb title and header disconnect action.
 * Pass null until that page's own connection response is ready. The mounted
 * path is frozen so an exiting page cannot claim the next route.
 */
export function useChannelDetailHeader(
  input: {
    title: string;
    disconnect: ChannelDetailDisconnectAction | null;
  } | null,
): void {
  const location = useLocation();
  const { organizationId, setRegisteredDetailHeader } = useChannelDirectory();
  const mountedPathRef = useRef(location.pathname);
  const onClickRef = useRef(input?.disconnect?.onClick);
  onClickRef.current = input?.disconnect?.onClick;
  const title = input?.title ?? null;
  const label = input?.disconnect?.label ?? null;
  const busy = input?.disconnect?.busy ?? false;
  const testId = input?.disconnect?.testId ?? null;

  useEffect(() => {
    const ownsCurrentRegistration = (
      current: RegisteredChannelDetailHeader | null,
    ): boolean => current?.pathname === mountedPathRef.current;

    if (location.pathname !== mountedPathRef.current || title == null) {
      setRegisteredDetailHeader((current) => (ownsCurrentRegistration(current) ? null : current));
      return;
    }

    setRegisteredDetailHeader({
      pathname: mountedPathRef.current,
      organizationId,
      title,
      disconnect:
        label != null && testId != null
          ? {
              label,
              busy,
              testId,
              onClick: () => {
                onClickRef.current?.();
              },
            }
          : null,
    });
  }, [
    setRegisteredDetailHeader,
    location.pathname,
    organizationId,
    title,
    label,
    busy,
    testId,
  ]);

  useEffect(() => {
    const ownedPath = mountedPathRef.current;
    return () => {
      setRegisteredDetailHeader((current) => (current?.pathname === ownedPath ? null : current));
    };
  }, [setRegisteredDetailHeader]);
}

const PANE_OFFSET_PX = 12;
const PANE_MS = 180;

export function ChannelsWorkspaceLayout({
  organizationId,
  role,
}: {
  organizationId: number;
  role: OrganizationMemberRoleName;
}): React.JSX.Element {
  const location = useLocation();
  const outlet = useOutlet();
  const reduceMotion = useReducedMotion() ?? false;
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [addChannelOpen, setAddChannelOpen] = useState(false);
  const [detailsPlatform, setDetailsPlatform] = useState<"facebook" | "instagram" | null>(null);
  const [whatsapp, setWhatsapp] = useState<WhatsAppConnectionResponse | null>(null);
  const [facebook, setFacebook] = useState<FacebookConnectionResponse | null>(null);
  const [instagram, setInstagram] = useState<InstagramConnectionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [registeredDetailHeader, setRegisteredDetailHeader] =
    useState<RegisteredChannelDetailHeader | null>(null);
  const detailPlatform = channelDetailPlatform(location.pathname);
  const appliedDetailHeader = useMemo(
    () =>
      applyChannelDetailHeader(
        resolveChannelDetailHeader({
          pathname: location.pathname,
          organizationId,
          registered: registeredDetailHeader,
          genericTitle: "",
        }),
        location.pathname,
      ),
    [location.pathname, organizationId, registeredDetailHeader],
  );
  const detailHeader = appliedDetailHeader.detailHeader;
  const detailHeaderPending = appliedDetailHeader.pending;

  const reload = useCallback(() => setReloadToken((value) => value + 1), []);
  const openMobileNav = useCallback(() => setMobileNavOpen(true), []);
  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);
  const openAddChannel = useCallback(() => setAddChannelOpen(true), []);
  const openDetails = useCallback((platform: "facebook" | "instagram") => {
    setDetailsPlatform(platform);
  }, []);
  const closeDetails = useCallback(() => setDetailsPlatform(null), []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    void Promise.all([
      getWhatsAppConnection(organizationId, controller.signal),
      getFacebookConnection(organizationId, controller.signal),
      getInstagramConnection(organizationId, controller.signal),
    ])
      .then(([nextWhatsapp, nextFacebook, nextInstagram]) => {
        if (controller.signal.aborted) return;
        setWhatsapp(nextWhatsapp);
        setFacebook(nextFacebook);
        setInstagram(nextInstagram);
        setLoading(false);
      })
      .catch((loadError: unknown) => {
        if (controller.signal.aborted) return;
        setWhatsapp(null);
        setFacebook(null);
        setInstagram(null);
        setLoading(false);
        setError(isApiError(loadError) ? loadError.message : "load");
      });
    return () => controller.abort();
  }, [organizationId, reloadToken]);

  useEffect(() => {
    setDetailsPlatform(null);
    setAddChannelOpen(false);
    setRegisteredDetailHeader(null);
  }, [organizationId]);

  const value = useMemo<ChannelDirectory>(
    () => ({
      organizationId,
      role,
      whatsapp,
      facebook,
      instagram,
      loading,
      error,
      reload,
      openMobileNav,
      mobileNavOpen,
      openAddChannel,
      addChannelOpen,
      setAddChannelOpen,
      detailsPlatform,
      openDetails,
      closeDetails,
      detailHeader,
      detailHeaderPending,
      setRegisteredDetailHeader,
    }),
    [
      organizationId,
      role,
      whatsapp,
      facebook,
      instagram,
      loading,
      error,
      reload,
      openMobileNav,
      mobileNavOpen,
      openAddChannel,
      addChannelOpen,
      detailsPlatform,
      openDetails,
      closeDetails,
      detailHeader,
      detailHeaderPending,
      setRegisteredDetailHeader,
    ],
  );

  const paneOffset = reduceMotion ? 0 : PANE_OFFSET_PX;

  return (
    <ChannelDirectoryContext.Provider value={value}>
      <div className="flex h-full min-h-0 bg-canvas" data-testid="channels-workspace">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <ChannelsWorkspaceHeader />
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetContent
              side="start"
              className="flex h-full max-h-dvh w-[min(100%,22rem)] flex-col gap-0 overflow-hidden border-e border-border/60 bg-surface p-0 text-foreground shadow-lg lg:w-[20rem]"
              overlayClassName={CONTACT_SHEET_OVERLAY_MOTION_CLASS}
              data-testid="channels-mobile-nav-sheet"
            >
              <ChannelsSidebar
                variant="drawer"
                collapsed={false}
                onNavigate={closeMobileNav}
                onClose={closeMobileNav}
              />
            </SheetContent>
          </Sheet>
          <div className="relative min-h-0 min-w-0 flex-1 overflow-hidden">
            <div
              className="absolute inset-0"
              aria-hidden={detailHeaderPending || undefined}
              inert={detailHeaderPending || undefined}
            >
              <AnimatePresence initial={false} mode="wait">
                <motion.div
                  key={location.pathname}
                  className="absolute inset-0 min-h-0 overflow-hidden"
                  initial={reduceMotion ? false : { opacity: 0, x: paneOffset }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: reduceMotion ? 0 : -paneOffset }}
                  transition={{
                    duration: reduceMotion ? 0 : PANE_MS / 1000,
                    ease: "easeOut",
                  }}
                >
                  {outlet}
                </motion.div>
              </AnimatePresence>
            </div>
            {detailHeaderPending && detailPlatform ? (
              <div className="absolute inset-0 bg-canvas">
                <ChannelDetailRouteSkeleton platform={detailPlatform} />
              </div>
            ) : null}
          </div>
        </div>
        <AddChannelDialog />
      </div>
    </ChannelDirectoryContext.Provider>
  );
}
